// Table definitions — keys = table names, values = index syntax used for enumeration.
const stores = {
  tillRegistrations: "++id",
  productCategories: "mProductCategoryId",
  productBrands: "brandId",
  newBrands: "brandId",
  dataSyncSummary: "++id, syncId, syncType",
  products: "++id, mProductId, mProductCategoryId, value, name, upc, brandId, isactive, productSegment, *batchIndex, *upcIndex",
  orders: "sOrderID, orderTime, documentno, isSynced, tillSessionId, customerSearchKey",
  pricingRules: "++id, billAmount, mPricingrulesId",
  posSaletypes: "++id, cwrSaletypeId",
  productUom: "++id, csUomId",
  tillEvents: "++id, tillSessionId, isSynced, tillStatus",
  logConfiguration: "++id",
  logInformation: "++id",
  restaurantTables: "++id",
  cashInCashOut: "++id",
  rfidData: "tag_value",
  tableData: "cwrFbTableId, name, tableSync",
  fbOrderData: "++id,fbOrderId,cwrFbTableId,fbOrderStatus,fbOrderSync",
  sectionTables: "++id",
  loyalityData: "loyaltylevelId",
  AllProductCategories: "mProductCategoryId",
  POSWorkFlowRules: "cwrRulesId",
  approvers: "++id",
  giftCardData: "cwrGiftcardTypeId",
  getExpenseCategory: "finExpCategoryId",
  getFinancialAccounts: "fin_financial_acct_id",
  paymentsData: "++id, tillSessionId, orderTime, tillStatus",
  ordersData: "++id, tillSessionId, orderTime, tillStatus",
  logActivity: "++id",
  salesRep: "s_salesrep_id, name,code",
  docTypesData: "++cs_doctype_id",
  getCompanionRules: "main_product_id",
  getRevenueCategories: "revenueCategoryId",
  pricingPolicies: "++id, dimensionCode, dimensionValue",
};

// ── In-Memory Search Index (supplements FTS5 for instant results) ──
let _sqliteSearchIndex = null;
let _sqliteSearchDirty = true;

function markSqliteSearchDirty() { _sqliteSearchDirty = true; _sqliteSearchIndex = null; }

async function buildSqliteSearchIndex() {
  if (_sqliteSearchIndex && !_sqliteSearchDirty) return;
  try {
    const products = await getElectronDB().getAll("products");
    _sqliteSearchIndex = products
      .filter((p) => p.isactive == true)
      .map((p) => ({
        _name: (p.name || "").toLowerCase(),
        _value: (p.value || "").toLowerCase(),
        _upc: (p.upc || "").toLowerCase(),
        _batch: (p.batchIndex || "").toLowerCase(),
        mProductCategoryId: p.mProductCategoryId,
        brandId: p.brandId,
        ref: p,
      }));
    _sqliteSearchDirty = false;
    console.debug(`[SQLiteAdapter] Search index built: ${_sqliteSearchIndex.length} products`);
  } catch { _sqliteSearchIndex = null; }
}

// Lazy accessor — only resolved when running inside Electron.
// This file is bundled in both browser and Electron builds, but
// createSQLiteAdapter() is never called in the browser, so the
// getter is never invoked there.
// Returns Electron DB bridge, or a no-op stub when running as a standalone web app
const noopDB = {
  query: async () => [],
  getAll: async () => [],
  get: async () => undefined,
  put: async () => {},
  bulkPut: async () => {},
  add: async () => {},
  bulkAdd: async () => {},
  delete: async () => {},
  deleteWhere: async () => 0,
  clear: async () => {},
  count: async () => 0,
};
const getElectronDB = () => window.electronAPI?.db || noopDB;

// Chainable collection that builds a query descriptor and resolves via IPC
class Collection {
  constructor(table, descriptor = {}) {
    this._table = table;
    this._descriptor = { ...descriptor };
  }

  // Terminal methods — execute the query via IPC

  async toArray() {
    return getElectronDB().query(this._table, { ...this._descriptor, terminal: "toArray" });
  }

  async first() {
    return getElectronDB().query(this._table, { ...this._descriptor, terminal: "first" });
  }

  async count() {
    return getElectronDB().query(this._table, { ...this._descriptor, terminal: "count" });
  }

  async keys() {
    return getElectronDB().query(this._table, { ...this._descriptor, terminal: "keys" });
  }

  async sortBy(field) {
    return getElectronDB().query(this._table, { ...this._descriptor, terminal: "toArray", sortBy: field });
  }

  async modify(changes) {
    if (typeof changes === "function") {
      // Function-based modify: fetch matching records, mutate in renderer, then put back
      const rows = await this.toArray();
      let modified = 0;
      for (const row of rows) {
        const original = JSON.stringify(row);
        changes(row);
        if (JSON.stringify(row) !== original) {
          await getElectronDB().put(this._table, row);
          modified++;
        }
      }
      return modified;
    }
    return getElectronDB().modify(this._table, this._descriptor, changes);
  }

  async delete() {
    return getElectronDB().query(this._table, { ...this._descriptor, terminal: "delete" });
  }

  // Chainable modifiers

  reverse() {
    return new Collection(this._table, { ...this._descriptor, reverse: true });
  }

  limit(n) {
    return new Collection(this._table, { ...this._descriptor, limit: n });
  }

  offset(n) {
    return new Collection(this._table, { ...this._descriptor, offset: n });
  }

  filter(fn) {
    // Serialize filter as a string for IPC (evaluated in main process)
    const filters = this._descriptor.filters || [];
    filters.push(fn.toString());
    return new Collection(this._table, { ...this._descriptor, filters });
  }

  and(fn) {
    return this.filter(fn);
  }

  // .or(field) — returns a WhereClause that adds an OR condition
  or(field) {
    return new WhereClause(this._table, field, this._descriptor);
  }
}

// WhereClause — returned by table.where(field)
class WhereClause {
  constructor(table, field, existingDescriptor) {
    this._table = table;
    this._field = field;
    this._existing = existingDescriptor || null;
  }

  _makeDescriptor(op, value) {
    const newWhere = { field: this._field, op, value };
    if (this._existing && this._existing.where) {
      // Combine with existing where as OR conditions
      const existing = this._existing.orWheres || [];
      const allOrs = [...existing, this._existing.where, newWhere];
      return { ...this._existing, where: allOrs[0], orWheres: allOrs.slice(1) };
    }
    return { where: newWhere };
  }

  equals(value) {
    return new Collection(this._table, this._makeDescriptor("equals", value));
  }

  equalsIgnoreCase(value) {
    return new Collection(this._table, this._makeDescriptor("equalsIgnoreCase", value));
  }

  notEqual(value) {
    return new Collection(this._table, this._makeDescriptor("notEqual", value));
  }

  above(value) {
    return new Collection(this._table, this._makeDescriptor("above", value));
  }

  aboveOrEqual(value) {
    return new Collection(this._table, this._makeDescriptor("aboveOrEqual", value));
  }

  below(value) {
    return new Collection(this._table, this._makeDescriptor("below", value));
  }

  belowOrEqual(value) {
    return new Collection(this._table, this._makeDescriptor("belowOrEqual", value));
  }

  between(lower, upper, includeLower = true, includeUpper = false) {
    return new Collection(this._table, this._makeDescriptor("between", { lower, upper, includeLower, includeUpper }));
  }

  anyOf(values) {
    return new Collection(this._table, this._makeDescriptor("anyOf", values));
  }

  noneOf(values) {
    return new Collection(this._table, this._makeDescriptor("noneOf", values));
  }

  startsWith(prefix) {
    return new Collection(this._table, this._makeDescriptor("startsWith", prefix));
  }

  startsWithIgnoreCase(prefix) {
    return new Collection(this._table, this._makeDescriptor("startsWithIgnoreCase", prefix));
  }

  startsWithAnyOf(prefixes) {
    return new Collection(this._table, this._makeDescriptor("startsWithAnyOf", prefixes));
  }

  startsWithAnyOfIgnoreCase(prefixes) {
    return new Collection(this._table, this._makeDescriptor("startsWithAnyOfIgnoreCase", prefixes));
  }
}

// Table proxy — provides .where(), .get(), .put(), .add(), etc.
class Table {
  constructor(name) {
    this._name = name;
  }

  get name() { return this._name; }

  where(fieldOrObj) {
    if (typeof fieldOrObj === "string") {
      return new WhereClause(this._name, fieldOrObj);
    }
    // where({field: value}) shorthand
    return new Collection(this._name, { where: { field: Object.keys(fieldOrObj)[0], op: "equals", value: Object.values(fieldOrObj)[0] } });
  }

  async get(key) {
    return getElectronDB().get(this._name, key);
  }

  async add(data) {
    return getElectronDB().add(this._name, data);
  }

  async put(data) {
    return getElectronDB().put(this._name, data);
  }

  async update(key, changes) {
    return getElectronDB().update(this._name, key, changes);
  }

  async delete(key) {
    return getElectronDB().delete(this._name, key);
  }

  async clear() {
    if (this._name === "products") markSqliteSearchDirty();
    return getElectronDB().clear(this._name);
  }

  async count() {
    return getElectronDB().count(this._name);
  }

  // Explicit search-index invalidation for single-row writes that bypass the
  // bulk paths (add/put/update and Collection.modify()). syncSingleProductByValue
  // writes one product at a time, so without this the in-memory index stays stale
  // and the freshly fetched product is invisible until the next bulk sync/reload.
  invalidateSearchIndex() { if (this._name === "products") markSqliteSearchDirty(); }

  async bulkPut(items) {
    const result = await getElectronDB().bulkPut(this._name, items);
    if (this._name === "products") { markSqliteSearchDirty(); buildSqliteSearchIndex(); }
    return result;
  }

  async bulkAdd(items) {
    const result = await getElectronDB().bulkAdd(this._name, items);
    if (this._name === "products") markSqliteSearchDirty();
    return result;
  }

  async bulkUpsert(items, keyField) {
    const result = await getElectronDB().bulkUpsert(this._name, items, keyField);
    if (this._name === "products") { markSqliteSearchDirty(); buildSqliteSearchIndex(); }
    return result;
  }

  /**
   * Product search — uses in-memory index for instant results,
   * falls back to FTS5/LIKE via IPC for complex queries.
   */
  async search(options) {
    const { searchTerm, limit = 50, categoryId, brandId } = options || {};
    if (searchTerm && this._name === "products") {
      // Lazily build the index if null (e.g. after page refresh) so that
      // the full product record — including uomData — is always used.
      // Without this, the FTS5 fallback returns only indexed columns,
      // causing uomData to be missing and isDecimalQty to be false.
      if (!_sqliteSearchIndex || _sqliteSearchDirty) {
        await buildSqliteSearchIndex();
      }
      if (_sqliteSearchIndex) {
        const term = searchTerm.toLowerCase();
        let results = _sqliteSearchIndex.filter((p) =>
          p._name.includes(term) || p._value.includes(term) || p._upc.includes(term) || p._batch.includes(term)
        );
        if (categoryId) results = results.filter((p) => p.mProductCategoryId === categoryId);
        if (brandId) results = results.filter((p) => p.brandId === brandId);
        return results.slice(0, limit).map((p) => p.ref);
      }
    }
    // Fallback: FTS5 via IPC (only reached if index build failed or no searchTerm)
    return getElectronDB().searchProducts(options);
  }

  async toArray() {
    return getElectronDB().getAll(this._name);
  }

  // Return a Collection for chaining
  toCollection() {
    return new Collection(this._name);
  }

  orderBy(field) {
    return new Collection(this._name, { sortBy: field });
  }

  filter(fn) {
    return new Collection(this._name).filter(fn);
  }

  limit(n) {
    return new Collection(this._name).limit(n);
  }

  reverse() {
    return new Collection(this._name).reverse();
  }
}

export function createSQLiteAdapter() {
  const tables = {};

  for (const tableName of Object.keys(stores)) {
    tables[tableName] = new Table(tableName);
  }

  // Public API surface
  const adapter = {
    ...tables,

    // Array of all table instances (used by db.tables.map(t => t.clear()))
    tables: Object.values(tables),

    // Utility methods
    table(name) {
      return tables[name] || new Table(name);
    },

    isOpen() {
      return true; // SQLite is always "open" via IPC
    },

    async open() {
      return adapter;
    },

    async close() {
      // No-op for IPC-based adapter
    },

    on(event, handler) {
      // No-op: SQLite via IPC doesn't need lifecycle events
    },

    async transaction(mode, tableNames, callback) {
      // SQLite transactions are handled in the main process
      // For the IPC adapter, we just execute the callback directly
      return callback(adapter);
    },
  };

  return adapter;
}
